import {deleteCookie, getCookie, setCookie} from '@github-ui/cookies'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {dismissUserNoticePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import {useGlobalNavAnalytics} from '../../utils/Analytics'

export interface Props {
  showTeamsBanner: boolean
}

export function TeamsBanner({showTeamsBanner}: Props) {
  const {sendItemClick} = useGlobalNavAnalytics()
  const teamsFeatureEnabled = isFeatureEnabled('global_nav_react_teams_settings_page')
  const [showBanner, setShowBanner] = useState(showTeamsBanner && teamsFeatureEnabled)

  const dismissTeamsBanner = useCallback(() => {
    setShowBanner(false)
    void verifiedFetch(dismissUserNoticePath({noticeName: 'global_nav_teams_banner'}), {method: 'POST'})
    deleteCookie('dismissed_teams_banner')
  }, [])

  const onClick = useCallback(() => {
    sendItemClick('TEAMS_BANNER_LINK')
  }, [sendItemClick])

  const onClickDismiss = useCallback(() => {
    dismissTeamsBanner()
    sendItemClick('TEAMS_BANNER_DISMISS')
  }, [dismissTeamsBanner, sendItemClick])

  useEffect(() => {
    if (showBanner) {
      const today = Date.now()
      const twoWeeksAgo = today - 14 * 24 * 60 * 60 * 1000
      const dismissedBannerDate = getCookie('dismissed_teams_banner')?.value
      if (dismissedBannerDate && parseInt(dismissedBannerDate, 10) < twoWeeksAgo) {
        // Banner was first seen more than two weeks ago, dismiss it
        // eslint-disable-next-line react-hooks/set-state-in-effect
        dismissTeamsBanner()
      } else if (!dismissedBannerDate) {
        // No cookie set, so show the banner and set the cookie to today
        setCookie('dismissed_teams_banner', today.toString())
      }
    }
  }, [dismissTeamsBanner, showBanner])

  if (!showBanner) {
    return null
  }

  return (
    <Banner variant="info" title="Teams have moved" hideTitle onDismiss={onClickDismiss} className="mx-2 my-3">
      <Banner.Description>
        Teams have moved to{' '}
        <a onClick={onClick} href="/settings/teams">
          settings
        </a>
      </Banner.Description>
    </Banner>
  )
}

try{ TeamsBanner.displayName ||= 'TeamsBanner' } catch {}